import React, { useMemo, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Box, Badge, Button, Flex, Spinner, Text } from 'theme-ui';
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import { ColumnDef } from '@tanstack/react-table';
import DataTable from "../Tabels/Tabel";
import { DescriptionItem, NotesModalProps } from "./DeliveryHistory.types";
import { bucketUrl } from '../../utils/global';
import { uploadIcon } from "../../helper/uploadType";
import { downloadAttachments, getResolvedTrade, getTabStyle } from "../../utils/GenerateQueryString";
import { linkifyText } from "../../utils/textFormat";
import { FaFileAlt, FaPaperclip } from 'react-icons/fa';
import { formatModalHeading } from "./deliveryHistory.utils";



// Stage/Rev/PC | Date | Delivery Note | Attachment
const COLUMN_WIDTHS = ['15%', '14%', '58%', '13%'];

const NotesDetailModal: React.FC<NotesModalProps> = ({
  isOpen,
  onClose,
  stageName,
  stageColor,
  descriptions,
  trades: tradesList,
  isDark,
  isLoading = false,
  closeButtonText,
}) => {
  const { stageAllTaskView, createprojectMasterData } = useSelector((state: Record<string, unknown>) => (state.project as { stageAllTaskView?: Record<string, unknown>[]; createprojectMasterData?: { allTask?: { data?: Array<Record<string, unknown>> } } }) || {});
  const stageTasks = useMemo(() => (Array.isArray(stageAllTaskView) ? stageAllTaskView : []), [stageAllTaskView]);
  const masterTasks = useMemo(() => createprojectMasterData?.allTask?.data || [], [createprojectMasterData]);

  const [activeTab, setActiveTab] = useState('All');
  const [attachmentModal, setAttachmentModal] = useState<{ open: boolean; item: DescriptionItem | null; attachments: string[] }>({
    open: false,
    item: null,
    attachments: [],
  });
  const [selectedModalIndices, setSelectedModalIndices] = useState<number[]>([]);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);

  const headerBg = isDark ? '#1a2135' : '#f8fafc';
  const borderColor = isDark ? '#334758' : '#e2e8f0';
  const accentColor = isDark ? '#BB86FC' : '#7743DB';
  const titleColor = isDark ? '#ffffff' : '#0f172a';
  const modalBg = isDark ? '#20283c' : '#ffffff';

  // Unique trade tabs derived from descriptions
  const trades = useMemo(() => {
    const seen = new Set<string>();
    (descriptions || []).forEach(d => {
      const resolved = getResolvedTrade(d, stageTasks, tradesList);
      if (resolved && resolved !== 'General') seen.add(resolved);
    });
    return ['All', ...Array.from(seen)];
  }, [descriptions, stageTasks]);

  const uniqueDescriptions = useMemo(() => {
    return (descriptions || []).filter((v, i, a) =>
      a.findIndex(t =>
        t.notes === v.notes &&
        getResolvedTrade(t, stageTasks, tradesList) === getResolvedTrade(v, stageTasks, tradesList) &&
        t.createdBy === v.createdBy &&
        t.date === v.date
      ) === i
    );
  }, [descriptions, stageTasks]);

  const filtered = useMemo(() => {
    if (activeTab === 'All') return uniqueDescriptions;
    return uniqueDescriptions.filter((data: DescriptionItem) => getResolvedTrade(data, stageTasks, tradesList) === activeTab);
  }, [activeTab, uniqueDescriptions, stageTasks]);



  const columns = useMemo<ColumnDef<DescriptionItem>[]>(() => [
    {
      header: "Stage / Rev/Pc",
      id: "stageRev",
      cell: (info) => {
        const item = info.row.original;
        const rev = item.stageRev || (item.trade ? item.trade.substring(0, 4).toUpperCase() : 'REV');
        const badgeColor = isDark ? '#BB86FC' : '#7743DB';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Badge sx={{
              bg: isDark ? 'rgba(119, 67, 219, 0.2)' : '#f0eafd',
              color: `${badgeColor} !important`,
              border: `1px solid ${badgeColor}`,
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 600,
              minWidth: '65px',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              px: '10px',
              py: '4px',
              whiteSpace: 'nowrap',
            }}>
              {rev}
            </Badge>
          </Box>
        );
      }
    },
    {
      header: "Date",
      accessorKey: "date",
      cell: (info) => {
        const item = info.row.original;
        const created = item.createdAt ? moment(item.createdAt) : null;
        if (!created || !created.isValid()) {
          return (
            <Box sx={{ textAlign: 'center', color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '13px', fontFamily: 'Poppins, sans-serif' }}>
              {item.date || ''}
            </Box>
          );
        }
        return (
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              color: isDark ? '#e2e8f0' : '#1e293b',
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            <Text>{created.format('MMM DD, YYYY')}</Text>
            <Text>{created.format('h:mm A')}</Text>
          </Flex>
        );
      }
    },
    {
      header: "Delivery Note",
      accessorKey: "notes",
      cell: (info) => {
        const item = info.row.original;
        return (
          <Box>
            <Text sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontFamily: 'Poppins, sans-serif', lineHeight: '1.55', wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
              {linkifyText(item.notes || '', accentColor)}
            </Text>
          </Box>
        );
      }
    },
    {
      header: "Attachment",
      id: "attachmentCol",
      cell: (info) => {
        const item = info.row.original;
        const attList: string[] = [];
        if (Array.isArray(item.attachment)) {
          item.attachment.forEach((a: string | unknown) => {
            if (typeof a === 'string' && a.trim()) attList.push(a.trim());
          });
        } else if (typeof item.attachment === 'string' && item.attachment.trim()) {
          attList.push(item.attachment.trim());
        }

        const attCount = attList.length;

        if (attCount === 0) {
          return null;
        }

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              onClick={() => {
                setAttachmentModal({ open: true, item, attachments: attList });
                setSelectedModalIndices([]);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                cursor: 'pointer',
                color: isDark ? '#818cf8 !important' : '#6366f1 !important',
                fontSize: '14px',
              }}
              title={attCount === 1 ? 'View attachment' : `View ${attCount} attachments`}
            >
              <FaPaperclip size={14} color={isDark ? '#818cf8' : '#6366f1'} style={{ color: isDark ? '#818cf8' : '#6366f1', fill: isDark ? '#818cf8' : '#6366f1' }} />
              {attCount > 0 && (
                <Text sx={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#818cf8 !important' : '#6366f1 !important' }}>{attCount}</Text>
              )}
            </Box>
          </Box>
        );
      }
    }
  ], [isDark, accentColor]);

  const closeBtn = (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      style={{
        color: titleColor,
        background: 'transparent',
        border: 'none',
        fontSize: 22,
        lineHeight: 1,
        padding: 0,
        cursor: 'pointer',
      }}
    >
      <span aria-hidden="true">&times;</span>
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        centered
        size="xl"
        toggle={onClose}
        backdrop="static"
        keyboard={false}
        className="notes-detail-modal"
        style={{ maxWidth: '93%', width: '93%' }}
        contentClassName="d-flex flex-column"
      >
        <ModalHeader
          close={closeBtn}
          toggle={onClose}
          style={{ backgroundColor: headerBg, borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}
        >
          <span
            style={{
              color: titleColor,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
            }}
          >
            DELIVERY NOTES
            {stageName && (
              <span style={{ color: (stageColor && stageColor !== '#000000' && stageColor !== '#000') ? stageColor : accentColor }}>
                {' '}— {formatModalHeading(stageName).toUpperCase()}
              </span>
            )}
          </span>
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: modalBg,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* ── Trade Tabs ─────────────────────────────────────────────────── */}
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '12px 16px 0',
              borderBottom: `1px solid ${borderColor}`,
              backgroundColor: headerBg,
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            {(trades || [])?.map((tab: string) => {
              const isActive = activeTab === tab;
              const tabStyle = getTabStyle(tab, isActive, isDark, accentColor, tradesList, masterTasks);
              return (
                <Box
                  as="button"
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  sx={{
                    padding: '6px 16px',
                    borderRadius: '8px 8px 0 0',
                    border: `1px solid ${tabStyle.border}`,
                    borderBottom: isActive
                      ? `2px solid ${tabStyle.border}`
                      : '2px solid transparent',
                    backgroundColor: tabStyle.bg,
                    color: tabStyle.color,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '13px',
                    cursor: 'pointer',
                    marginBottom: '-1px',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab}
                </Box>
              );
            })}
          </Box>

          {/* ── Notes Table / List ──────────────────────────────────────────── */}
          <Box
            sx={{
              p: '8px 16px',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              '& thead th': { position: 'sticky', top: 0, zIndex: 1 },
              '& table': { tableLayout: 'fixed', width: '100%' },
              '& tbody td': { padding: '6px 10px !important' },
              ...Object.fromEntries(COLUMN_WIDTHS.map((width, i) => [`& th:nth-of-type(${i + 1})`, { width }])),
              // single scroll area: drop the table's own max-height / scroll so it never nests inside this box
              '& .tableFixHead, & .tableNotFixHead': { maxHeight: 'none !important', minHeight: '0 !important', overflow: 'visible' },
            }}
          >
            {isLoading ? (
              <Flex sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Spinner size={36} />
              </Flex>
            ) : filtered.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  padding: '40px',
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                }}
              >
                No delivery notes available.
              </Box>
            ) : (
              <DataTable
                data={filtered}
                columns={columns}
                showNavigation={false}
                onPageChange={() => { }}
                onPerPageChange={() => { }}
                perPageCount={filtered.length || 10}
                TotalLength={filtered.length}
                enableColumnFilters={false}
                noDataCentered={true}
                inlineLoader={false}
              />
            )}
          </Box>
        </ModalBody>

        <ModalFooter
          style={{
            backgroundColor: headerBg,
            borderTop: `1px solid ${borderColor}`,
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button
            onClick={onClose}
            variant="buttons.CancelButton"
          >
            {closeButtonText || 'Close'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Manage Attachments Modal */}
      {attachmentModal.item && (
        <Modal
          isOpen={attachmentModal.open}
          toggle={() => setAttachmentModal({ open: false, item: null, attachments: [] })}
          backdrop="static"
          keyboard={false}
          centered
          size="md"
        >
          <ModalHeader
            toggle={() => setAttachmentModal({ open: false, item: null, attachments: [] })}
            style={{ backgroundColor: modalBg, color: titleColor }}
          >
            Manage Attachments
          </ModalHeader>
          <ModalBody style={{ backgroundColor: modalBg, color: titleColor, padding: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {attachmentModal.attachments.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text sx={{ fontWeight: 600, fontSize: '14px', color: titleColor }}>Current Attachments:</Text>
                    {selectedModalIndices.length > 0 && (
                      <Button
                        type="button"
                        disabled={isDownloadingFiles}
                        onClick={async () => {
                          const urls = selectedModalIndices.map(i => attachmentModal.attachments[i]).filter(Boolean);
                          if (!urls.length) return;
                          setIsDownloadingFiles(true);
                          try {
                            await downloadAttachments(urls);
                          } catch {
                            /* ignore */
                          } finally {
                            setIsDownloadingFiles(false);
                          }
                        }}
                        sx={{
                          fontSize: '12px',
                          padding: '4px 10px',
                          backgroundColor: '#7743DB',
                          color: '#ffffff',
                          borderRadius: '4px',
                          cursor: isDownloadingFiles ? 'not-allowed' : 'pointer',
                          border: 'none',
                          opacity: isDownloadingFiles ? 0.7 : 1,
                        }}
                      >
                        {isDownloadingFiles ? 'Downloading...' : selectedModalIndices.length === 1 ? 'Download' : 'Download All'}
                      </Button>
                    )}
                  </Flex>

                  {selectedModalIndices.length === 0 && (
                    <Text sx={{ fontSize: '13px', color: '#ef4444', fontStyle: 'italic', marginTop: '-4px' }}>
                      * Please select checkbox to download attachments
                    </Text>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto', padding: '2px' }}>
                    {attachmentModal.attachments.map((url, idx) => {
                      const parts = url.split('/');
                      const filename = parts[parts.length - 1];
                      const ext = filename.split('.').pop()?.toLowerCase() || '';
                      const iconSrc = uploadIcon(ext);
                      const fullUrl = url.startsWith('http') ? url : `${bucketUrl}${url}`;

                      return (
                        <Flex
                          key={idx}
                          sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: isDark ? '1px solid #374151' : '1px solid #E2E8F0',
                            backgroundColor: isDark ? 'rgba(13,202,240,0.12)' : 'rgba(248,249,250,1)',
                            gap: '12px',
                          }}
                        >
                          <Flex sx={{ alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                            <input
                              type="checkbox"
                              checked={selectedModalIndices.includes(idx)}
                              onChange={() => setSelectedModalIndices(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx])}
                              style={{ cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px', accentColor: '#7743DB' }}
                            />
                            {iconSrc ? (
                              <img src={iconSrc} alt={ext} style={{ width: '22px', height: '22px', objectFit: 'contain', flexShrink: 0 }} />
                            ) : (
                              <FaFileAlt size={20} color="#0dcaf0" style={{ flexShrink: 0 }} />
                            )}
                            <a
                              href={fullUrl}
                              target="_blank"
                              rel="noreferrer"
                              title={filename}
                              style={{
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#0dcaf0',
                                textDecoration: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '280px',
                                display: 'inline-block',
                              }}
                            >
                              {`Link ${idx + 1}`}
                            </a>
                          </Flex>
                        </Flex>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Text sx={{ fontSize: '14px', color: '#8F9195' }}>No attachments available.</Text>
              )}

              <Flex sx={{ justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  onClick={() => setAttachmentModal({ open: false, item: null, attachments: [] })}
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#333333',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    px: '20px',
                    py: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f3f4f6' },
                  }}
                >
                  Cancel
                </Button>
              </Flex>
            </Box>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default NotesDetailModal;
